// Borlabs -> Shopify consent reconciliation.
// Borlabs (WordPress parent domain) is the master consent source. On every Shopify
// page load the Borlabs cookie is read, mapped to the four Shopify Customer Privacy
// categories and written to Shopify if it differs from the CURRENT Shopify consent
// state. This corrects a stale Shopify state left behind e.g. by an earlier checkout.
//
// Debug logging is gated behind `window.borlabsConsentDebug` (set it to a truthy value
// to see the verbose trace). Errors are always logged.

const DEBUG = () => !!window.borlabsConsentDebug;
const log = (...args) => {
	if (DEBUG()) console.log('[consent]', ...args);
};
const warn = (...args) => {
	if (DEBUG()) console.warn('[consent]', ...args);
};

log('Consent handler loaded');

const MAX_WAIT_TIME = 10000; // 10 seconds
const RETRY_INTERVAL = 200; // 200 milliseconds

// `label` is deliberately the LAST parameter: it is purely additive and cannot shift the
// meaning of an existing call. Without it every one of the three call sites logged the same
// "Timeout reached while waiting for condition", so the log never said which condition
// actually failed — that is the message from the customer ticket.
const pollUntil = (conditionFunction, onSuccess, onFailure, timeout = MAX_WAIT_TIME, label = 'condition') => {
	const startTime = Date.now();
	const interval = setInterval(() => {
		log(`Polling for ${label}...`);
		if (conditionFunction()) {
			clearInterval(interval);
			onSuccess();
		} else if (Date.now() - startTime > timeout) {
			clearInterval(interval);
			console.error(`[consent] Timeout after ${timeout} ms waiting for ${label}`);
			if (onFailure) {
				onFailure();
			}
		}
	}, RETRY_INTERVAL);
};
const publishEventToShopifyFromParam = (param) => {
	const eventName = 'post:paramsDataId';
	const eventData = {
		event: eventName,
		data: param,
	};
	pollUntil(
		() => window.Shopify && window.Shopify.analytics,
		() => {
			window.Shopify.analytics.publish(eventName, eventData);
			log('Shopify object is available, event published:', eventName, eventData);
		},
		// Deliberately NOT failConsent(): this is about marketing parameters, not the consent
		// state. Signalling "consent settled" from here would be semantically wrong.
		() => {
			console.error('[consent] Failed to publish event to Shopify after retries');
		},
		MAX_WAIT_TIME,
		'Shopify.analytics',
	);
};
const getCookie = (key) => {
	const decodedCookie = decodeURIComponent(document.cookie);
	const ca = decodedCookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		const cKey = c.split('=')[0].trim();

		if (cKey === key) {
			const value = c.split('=')[1].trim();
			log(`Cookie found -> ${cKey}`);
			return value;
		}
	}
	return '';
};
const getBorlabsCookie = () => {
	const cookieValue = getCookie('borlabs-cookie');
	if (!cookieValue) {
		log('No borlabs-cookie found');
		return null;
	}
	try {
		const decoded = decodeURIComponent(cookieValue);
		const parsed = JSON.parse(decoded);
		log('Borlabs cookie parsed:', parsed);
		return parsed;
	} catch (error) {
		console.error('[consent] Error parsing borlabs-cookie:', error);
		return null;
	}
};
const mapBorlabsToShopifyConsent = (borlabsData) => {
	if (!borlabsData || !borlabsData.consents) {
		return null;
	}
	const statisticsConsents = borlabsData.consents.statistics;
	const marketingConsents = borlabsData.consents.marketing;
	const preferencesConsents = borlabsData.consents.preferences;
	const requiredVendors = window.borlabsMarketingVendors || ['meta', 'google'];
	// "all required" rule for preferences as well: every configured preferences vendor
	// must be present in the Borlabs preferences group. Vendor list is configurable via
	// window.borlabsPreferencesVendors (theme setting). See docs/consent-mapping-P26144.md.
	const requiredPreferencesVendors = window.borlabsPreferencesVendors || [];
	return {
		analytics: Array.isArray(statisticsConsents) && statisticsConsents.includes('mapp'),
		marketing:
			Array.isArray(marketingConsents) && requiredVendors.every((vendor) => marketingConsents.includes(vendor)),
		// preferences stays false while no preferences vendors are configured (no vacuous true).
		preferences:
			requiredPreferencesVendors.length > 0 &&
			Array.isArray(preferencesConsents) &&
			requiredPreferencesVendors.every((vendor) => preferencesConsents.includes(vendor)),
		sale_of_data: false,
	};
};
// Signals that the consent state is final for THIS page load: nothing will change it any
// more and no consent reload is pending. cart-link.js waits for this before publishing the
// cart view, so the view never carries a pre-sync consent state and is never sent twice.
// Fires at most once per page load.
let consentSettledDispatched = false;
// Set the moment a reload is decided on, BEFORE location.reload() is called. Once a reload
// is under way nothing about this page load is final any more, so no consumer may act on a
// "settled" signal: the reload would tear their work down. Verified on the preview theme —
// without this flag the cart add started and was aborted mid-request by the reload, and the
// reloaded page found the buffer already consumed. The sessionStorage key alone cannot carry
// this, because the reload path sets it *before* the pending write callbacks run: those then
// see "no reload pending" and signal exactly at the wrong moment.
let consentReloadStarted = false;
const beginConsentReload = (reason) => {
	consentReloadStarted = true;
	warn(`Reloading page: ${reason}`);
	sessionStorage.setItem('consentReloadedOnce', '1');
	window.location.reload();
};
const signalConsentSettled = () => {
	if (consentSettledDispatched || consentReloadStarted) return;
	consentSettledDispatched = true;
	log('Consent settled');
	document.dispatchEvent(new CustomEvent('consent:settled', { bubbles: true }));
};
// A consent write is followed by a reload — either from the visitorConsentCollected handler
// at the bottom of this file or from the 4s fallback in setShopifyConsent — unless this
// session has already reloaded once. While that reload is pending the consent is NOT final
// for this page load; the reloaded page signals it instead.
const consentReloadPending = () => !consentReloadStarted && !sessionStorage.getItem('consentReloadedOnce');
// A failed consent sync is a result, not a standstill. Previously the failure paths only
// wrote to the console: nothing downstream was told, so the cart kept waiting for a signal
// that never came. This logs visibly, announces `consent:failed` (monitoring or UI can
// listen) and closes the consent process for this page load.
// `terminal: true` means the sync is over and did NOT happen — consumers may act on it.
// The load-features branch below reports `terminal: false` instead, because the write is
// still attempted afterwards and may well succeed.
const failConsent = (stage, message, error) => {
	console.error(`[consent] ${message} (stage: ${stage})`, error || '');
	document.dispatchEvent(new CustomEvent('consent:failed', { bubbles: true, detail: { stage, terminal: true } }));
	signalConsentSettled();
};
const setShopifyConsent = (consentData, source) => {
	pollUntil(
		() => window.Shopify && typeof window.Shopify.customerPrivacy === 'object',
		() => {
			window.Shopify.customerPrivacy.setTrackingConsent(consentData, () => {
				log(`Consent set via ${source}`);
				if (!consentReloadPending()) {
					signalConsentSettled();
				}
			});
			setTimeout(() => {
				if (consentReloadPending()) {
					beginConsentReload('fallback, consent event not received');
				}
			}, 4000); // 4 seconds fallback
		},
		() => {
			failConsent(
				'customer-privacy-unavailable',
				'Consent konnte nicht geschrieben werden: Shopify.customerPrivacy war nicht verfuegbar',
			);
		},
		MAX_WAIT_TIME,
		'Shopify.customerPrivacy',
	);
};
// Coerce Shopify Customer Privacy values (which may be 'yes' | 'no' | '' | boolean) to a boolean.
const toBool = (value) => value === true || value === 'yes';
// Read the CURRENT Shopify consent as a normalized boolean object, or null if unavailable.
const getCurrentShopifyConsent = () => {
	try {
		const current = window.Shopify.customerPrivacy.currentVisitorConsent();
		return {
			analytics: toBool(current.analytics),
			marketing: toBool(current.marketing),
			preferences: toBool(current.preferences),
			sale_of_data: toBool(current.sale_of_data),
		};
	} catch (error) {
		// Visible, not gated behind window.borlabsConsentDebug: this failure makes the handler
		// conclude "consent differs", write unnecessarily and trigger a reload. The behaviour
		// stays as it is — writing on an unknown Shopify state is the correct, fail-closed
		// choice for a master source. The bug was that nobody could see it happen.
		console.error('[consent] Could not read current Shopify consent, assuming mismatch:', error);
		return null;
	}
};
const consentMatches = (a, b) =>
	!!a &&
	!!b &&
	a.analytics === b.analytics &&
	a.marketing === b.marketing &&
	a.preferences === b.preferences &&
	a.sale_of_data === b.sale_of_data;
const setShopifyConsentFromBorlabs = () => {
	const borlabsData = getBorlabsCookie();
	if (!borlabsData) {
		// Cookie present but unreadable: no write, no reload — the consent stays as it is.
		log('No borlabs cookie data available');
		signalConsentSettled();
		return false;
	}
	const consentData = mapBorlabsToShopifyConsent(borlabsData);
	if (!consentData) {
		log('Could not map borlabs consent data');
		signalConsentSettled();
		return false;
	}
	log('Mapped borlabs consent:', consentData);
	// Borlabs is the master: compare the mapped Borlabs consent against the CURRENT Shopify
	// consent state (NOT against the last Borlabs sync). This way a stale Shopify state — e.g.
	// changed during an earlier checkout while the Borlabs cookie stayed the same — is detected
	// and corrected on the next page load.
	const currentShopify = getCurrentShopifyConsent();
	if (consentMatches(consentData, currentShopify)) {
		log('Shopify consent already matches Borlabs, skipping write');
		document.dispatchEvent(new CustomEvent('consent:already-synced', { bubbles: true }));
		signalConsentSettled();
		return false;
	}
	log('Shopify consent differs from Borlabs, overwriting (Borlabs = master)', {
		currentShopify,
		borlabs: consentData,
	});
	setShopifyConsent(consentData, 'borlabs cookie');
};
document.addEventListener('consent:params', function (event) {
	log('Consent param event received:', event.detail);

	const urlParams = event.detail.params;
	// marketing technology params
	const paramIDs = ['gbraid', 'wbraid', 'gclid', 'fbclid', 'utm_source', 'everID'];
	const idParamFound = paramIDs.find((param) => urlParams.has(param));
	if (idParamFound) {
		const param = {
			name: idParamFound,
			value: urlParams.get(idParamFound),
		};
		publishEventToShopifyFromParam(param);
	} else {
		log('No marketing param found');
	}
	// Read consent from borlabs cookie
	pollUntil(
		() => window.Shopify && typeof window.Shopify.loadFeatures === 'function',
		() => {
			log('Shopify consent API is available');
			window.Shopify.loadFeatures(
				[
					{
						name: 'consent-tracking-api',
						version: '0.1',
					},
				],
				(err) => {
					if (err) {
						// The feature script can be blocked by a tracking blocker — documented in
						// docs/consent-mapping-P26144.md as `createScriptTag → onError`. Until now the
						// handler returned here and never transferred the Borlabs consent at all. Now
						// the write is still attempted: if Shopify.customerPrivacy already exists it
						// succeeds anyway, and if it does not, the failure path in setShopifyConsent
						// closes the process properly. No signalConsentSettled() here —
						// setShopifyConsentFromBorlabs() closes it in every one of its branches, and
						// signalling now would report "done" too early.
						console.error('[consent] Error loading Shopify features:', err);
						document.dispatchEvent(
							new CustomEvent('consent:failed', {
								bubbles: true,
								detail: { stage: 'load-features-error', terminal: false },
							}),
						);
					} else {
						log('Shopify consent-tracking feature loaded');
					}
					setShopifyConsentFromBorlabs();
				},
			);
		},
		() => {
			failConsent('load-features-unavailable', 'Shopify.loadFeatures war nicht verfuegbar');
		},
		MAX_WAIT_TIME,
		'Shopify.loadFeatures',
	);
});

document.addEventListener('visitorConsentCollected', (event) => {
	log('Consent set', event.detail);
	const consentData = {
		analyticsAllowed: event.detail?.analyticsAllowed || false,
		marketingAllowed: event.detail?.marketingAllowed || false,
		preferencesAllowed: event.detail?.preferencesAllowed || false,
	};
	if (!window._saleOfDataOverrideApplied) {
		// Re-set consent with sale_of_data explicitly false (Shopify banner doesn't include it)
		window._saleOfDataOverrideApplied = true;
		setShopifyConsent(
			{
				analytics: consentData.analyticsAllowed,
				marketing: consentData.marketingAllowed,
				preferences: consentData.preferencesAllowed,
				sale_of_data: false,
			},
			'shopify banner (sale_of_data override)',
		);
		return;
	} else {
		window._saleOfDataOverrideApplied = false;
	}
	// reload the page to apply the consent
	if (!consentReloadPending()) return;
	beginConsentReload('consent collected');
});
